import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { take } from 'rxjs';
import { AppRoutes, DrillScoring, NAVIGATION_TOKEN, NavigationProvider } from '@core';
import {
  DrillListItemComponent,
  DrillsListQueryHandler,
  DrillsListQueryResult,
  DrillsListTab,
  DrillsListTabsComponent,
  DrillsListTypeFilter,
  DrillsListTypeFilterComponent,
  DrillViewModel,
  TargetRunsFormComponent
} from '@drills';
import { DrillType } from '@drills/infra';
import {
  SzAppShellComponent,
  SzBottomSheetComponent,
  SzButtonComponent,
  SzIconComponent,
  SzPageTitleComponent,
  SzSearchInputComponent,
  SzSectionHeadComponent,
  SzSkeletonComponent,
  SzTopBarComponent
} from '@ui';
import { SessionStore } from '../../application/state';

@Component({
  selector: 'lib-session-add-drill',
  templateUrl: './session-add-drill.component.html',
  styleUrls: ['./session-add-drill.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    SzAppShellComponent,
    SzTopBarComponent,
    SzPageTitleComponent,
    SzSearchInputComponent,
    SzSectionHeadComponent,
    SzSkeletonComponent,
    SzBottomSheetComponent,
    SzButtonComponent,
    SzIconComponent,
    DrillListItemComponent,
    DrillsListTypeFilterComponent,
    DrillsListTabsComponent,
    TargetRunsFormComponent
  ],
  providers: [DrillsListQueryHandler]
})
export class SessionAddDrillComponent implements OnInit {
  private readonly router: Router = inject(Router);
  private readonly navigation: NavigationProvider = inject(NAVIGATION_TOKEN);
  private readonly listHandler: DrillsListQueryHandler = inject(DrillsListQueryHandler);
  private readonly store: SessionStore = inject(SessionStore);

  readonly data: WritableSignal<DrillsListQueryResult | null> = signal<DrillsListQueryResult | null>(null);
  readonly searchQuery: WritableSignal<string> = signal<string>('');
  readonly activeTab: WritableSignal<DrillsListTab> = signal<DrillsListTab>('all');
  readonly typeFilter: WritableSignal<DrillsListTypeFilter> = signal<DrillsListTypeFilter>('all');

  readonly selectedDrill: WritableSignal<DrillViewModel | null> = signal<DrillViewModel | null>(null);

  readonly visibleDrills: Signal<readonly DrillViewModel[]> = computed((): readonly DrillViewModel[] => {
    const result: DrillsListQueryResult | null = this.data();
    if (result === null) return [];
    const tab: DrillsListTab = this.activeTab();
    const tabFiltered: readonly DrillViewModel[] =
      tab === 'favorites' ? result.favorites : tab === 'recent' ? result.recent : result.all;
    const typeFiltered: readonly DrillViewModel[] = this.applyTypeFilter(tabFiltered, this.typeFilter());
    const q: string = this.searchQuery().trim().toLowerCase();
    return q.length === 0 ? typeFiltered : typeFiltered.filter((d: DrillViewModel) => d.name.toLowerCase().includes(q));
  });

  ngOnInit(): void {
    if (this.store.snapshot() === null) {
      void this.router.navigate(['/', AppRoutes.DRILLS], { replaceUrl: true });
      return;
    }
    this.loadDrills();
  }

  setSearch(value: string): void {
    this.searchQuery.set(value);
  }

  setTab(tab: DrillsListTab): void {
    this.activeTab.set(tab);
  }

  setTypeFilter(filter: DrillsListTypeFilter): void {
    this.typeFilter.set(filter);
  }

  select(drill: DrillViewModel): void {
    if (drill.scoring === DrillScoring.TimePerRep || drill.scoring === DrillScoring.HitFactor) {
      this.selectedDrill.set(drill);
      return;
    }
    this.addAndReturn(drill, null);
  }

  closeSheet(): void {
    this.selectedDrill.set(null);
  }

  onRunsTargetSubmit(target: number | null): void {
    const drill: DrillViewModel | null = this.selectedDrill();
    if (drill === null) return;
    this.addAndReturn(drill, target);
  }

  private addAndReturn(drill: DrillViewModel, targetRuns: number | null): void {
    this.store.addDrill({
      drillId: drill.id,
      drillName: drill.name,
      drillDescription: drill.description,
      planNote: null,
      scoring: drill.scoring,
      shotsCount: drill.shotsCount,
      targetRuns,
      runs: []
    });
    this.selectedDrill.set(null);
    this.navigation.back();
  }

  private loadDrills(): void {
    this.listHandler
      .query()
      .pipe(take(1))
      .subscribe((r: DrillsListQueryResult) => this.data.set(r));
  }

  private applyTypeFilter(drills: readonly DrillViewModel[], filter: DrillsListTypeFilter): readonly DrillViewModel[] {
    if (filter === 'all') return drills;
    const target: DrillType = filter === 'dry' ? DrillType.Dry : DrillType.Live;
    return drills.filter((d: DrillViewModel) => d.type === target);
  }
}
